import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ImpulseSpinner } from 'react-spinners-kit';
import QRCodeRaw from 'qrcode.react';
import * as actions from '~actions';
import Button from '../../Scenes/Tables/Components/Button';
import { useAdyen, useDeviceInfo, useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { BlueButton, Box, RedButton } from '~Styles';
import AdyenWizard from '~Components/Wizards/AdyenWizard';
import { useStorage } from '~Components/Storage';
import CancelButton from './CancelButton';

const PaymentStatusWrapper = styled(Box)`
  padding: 20px 0;
  padding-bottom: 35px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: 112px;
  & > * {
    color: inherit;
  }
`;

const QRCode = styled(QRCodeRaw)`
  width: 175px;
  height: 175px;
  margin-top: 30px;
  border: 25px solid #fff;
  background: #fff;
`;

type PaymentStatusProps = {
  status: 'PENDING' | 'FAILED' | 'COMPLETED';
  paymentId: string;
  isSplitting?: boolean;
  retryRefund?: () => void;
  isRefunding?: boolean;
  textColor?: 'colorWhite' | 'colorBlack';
  onlyShowStatus?: boolean;
};

const PaymentStatus = ({
  status,
  paymentId,
  isSplitting,
  retryRefund,
  isRefunding,
  textColor = 'colorWhite',
  onlyShowStatus,
}: PaymentStatusProps) => {
  const logger = useLogger('payment-status');
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const { abortPayment } = useAdyen();
  const [storage] = useStorage();
  const deviceInfo = useDeviceInfo();
  const adyenTerminals = storage.adyenTerminals;
  const [isAborting, setIsAborting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const payment = useSelector((state) => state.POSPayments.find((p) => p.id === paymentId));

  useEffect(() => {
    if (!payment?.hasConnectionIssues || showHelp) {
      return;
    }

    logger.info('Payment has connection issues', { payment });
    setShowHelp(true);
  }, [payment?.hasConnectionIssues, showHelp]);

  if (!status) {
    return null;
  }

  if (payment?.method === 'PAY_LATER') {
    return (
      <PaymentStatusWrapper color={textColor}>
        <ImpulseSpinner size={50} />
      </PaymentStatusWrapper>
    );
  }

  const cancelPayment = (forceFail?: boolean) => {
    if (forceFail) {
      dispatch({
        type: actions.POS_PAYMENT_FAILED,
        id: paymentId,
        error: i18n('PaymentStatus.Cancelled'),
      });
      return;
    }

    if (payment?.method === 'ADYEN' && payment.meta.serviceId && payment.variant) {
      setIsAborting(true);
      abortPayment({ serviceId: payment.meta.serviceId, terminalId: payment.variant }).then(
        ({ success }) => {
          logger.info('Aborting payment', { success });
          // If unsuccessful, activate button for retrying
          if (!success) {
            setIsAborting(false);
          }
        },
      );
    } else if (payment?.method === 'CRYPTO') {
      setIsAborting(true);
      dispatch(actions.updatePaymentMeta(paymentId, { cancelRequested: true }));
    }
  };

  const actionType =
    isSplitting && isRefunding ? i18n('PaymentStatus.Refund') : i18n('PaymentStatus.Payment');

  if (status === 'PENDING' && payment?.method === 'ADYEN' && adyenTerminals.length === 0) {
    return (
      <PaymentStatusWrapper color={textColor}>
        <Box marginBottom="small" style={{ textAlign: 'center' }} padding="small">
          {i18n('PaymentStatus.NoTerminal')}
        </Box>
        <Box display="flex" flexDirection="column" justifyContent="center">
          <BlueButton onTouchStart={() => setShowWizard(true)} marginBottom="small">
            {i18n('PaymentStatus.NoTerminal.Add')}
          </BlueButton>
          <RedButton onTouchStart={() => cancelPayment(true)}>
            {i18n('PaymentStatus.NoTerminal.Cancel')}
          </RedButton>
        </Box>
        {showWizard && (
          <AdyenWizard
            close={() => setShowWizard(false)}
            cancelPayment={() => cancelPayment(true)}
          />
        )}
      </PaymentStatusWrapper>
    );
  }

  if (status === 'PENDING') {
    return (
      <PaymentStatusWrapper color={textColor}>
        <Box marginBottom="medium">
          {actionType} {i18n('PaymentStatus.InProgress')}
        </Box>
        <ImpulseSpinner size={50} />
        {payment && payment.meta.qr && !onlyShowStatus && (
          <>
            <QRCode size={150} value={payment.meta.qr} />
            <CancelButton
              onClick={() => cancelPayment(false)}
              background="var(--colorUIRed)"
              disabled={isAborting}>
              {isAborting ? i18n('Adyen.PleaseWait') : i18n('General.Cancel')}
            </CancelButton>
          </>
        )}
        {showHelp && (
          <Box marginTop="medium" style={{ width: '200px' }}>
            <Button
              slim
              indented
              onTouchStart={() => {
                setShowWizard(true);
                logger.info('Payment has connection issues, user clicked help', { payment });
              }}>
              {i18n('PaymentStatus.NeedHelp')}
            </Button>
          </Box>
        )}
        {payment?.meta.terminalStatus && (
          <Box marginTop="medium" style={{ width: '200px', textAlign: 'center' }}>
            {i18n(`Adyen.TerminalStatus.${payment.meta.terminalStatus}`)}
          </Box>
        )}
        {payment && ['ADYEN'].includes(payment.method) && !deviceInfo?.isAdyenTerminal && (
          <CancelButton
            onClick={cancelPayment}
            background="var(--colorUIRed)"
            disabled={isAborting}>
            {i18n('General.Cancel')}
          </CancelButton>
        )}
        {showWizard && (
          <AdyenWizard
            close={() => setShowWizard(false)}
            cancelPayment={() => cancelPayment(true)}
          />
        )}
      </PaymentStatusWrapper>
    );
  }

  if (status === 'FAILED') {
    return (
      <>
        <PaymentStatusWrapper color={textColor}>
          <Box marginBottom="medium">
            {actionType} {i18n('PaymentStatus.Cancelled')}
          </Box>

          {payment?.message && <div>{payment.message}</div>}
        </PaymentStatusWrapper>
        {isSplitting && isRefunding && retryRefund && (
          <Button onTouchStart={retryRefund}>{i18n('PaymentStatus.RetryRefund')}</Button>
        )}
      </>
    );
  }

  return (
    <PaymentStatusWrapper color={textColor}>
      <Box marginBottom="medium">
        {isSplitting
          ? `${i18n('PaymentStatus.ReadyForNext')} ${actionType.toLowerCase()}`
          : `${actionType} ${i18n('PaymentStatus.Completed')}`}
      </Box>
    </PaymentStatusWrapper>
  );
};

export default PaymentStatus;
